/* @flow */
import _ from 'lodash';
import memoize from 'memoizee';

import Chat from 'entities/Chat';
import Chats from 'collections/Chats';
import ChatMessage from 'entities/ChatMessage';
import ChatMembership from 'entities/ChatMembership';
import { dispatcher, reduxDispatcher } from 'lib/Dispatcher';
import Group from 'entities/Group';
import PotentialChatMember from 'entities/PotentialChatMember';
import PotentialChatMembers from 'collections/PotentialChatMembers';
import ToastActions from 'actions/ToastActions';
import { ActionTypes,
        CHATS_PAGE_SIZE,
        SEARCH_NETWORK_LIMIT_MS,
        DATA_RELOAD_TIMEOUT_MS } from 'chat/constants/ChatConstants';
import { rootGroup } from 'entities/RootGroup';

import type { PotentialOrRegularChatMember } from 'entities/PotentialChatMember';

type LoadedRange = {
  oldestUpdate: ?Date,
  newestUpdate: ?Date,
};

const loadedChatRangeMap = {
  map: {},
  getLoadedChatRangeForGroup: function(groupId: number): LoadedRange { // eslint-disable-line
    if (!this.map[groupId]) {
      this.map[groupId] = {
        oldestUpdate: null,
        newestUpdate: null,
      };
    }

    return this.map[groupId];
  },
  setLoadedChatRangeForGroup: function(groupId: number, chats: Chats): void { // eslint-disable-line
    if (chats.isEmpty()) {
      return;
    }

    const existing = this.getLoadedChatRangeForGroup(groupId);

    const oldestUpdate = chats.getOldestUpdatedAt();
    if (oldestUpdate && (!existing.oldestUpdate || oldestUpdate < existing.oldestUpdate)) {
      existing.oldestUpdate = oldestUpdate;
    }

    const newestUpdate = chats.getNewestUpdatedAt();
    if (newestUpdate && (!existing.newestUpdate || newestUpdate > existing.newestUpdate)) {
      existing.newestUpdate = newestUpdate;
    }
  },
};

const ChatActions = {
  createChat(chatToInitiate: Chat, message: ChatMessage): Promise<Chat> {
    return chatToInitiate.initiate(message).then(chat => {
      dispatcher.dispatch({
        type: ActionTypes.CREATE_CHAT,
        chat,
      });

      // set the chat id on the message.
      // this is necessary because the created chat may
      // not have the same uuid as the chat given in the parameters
      // if there was a preexisting one to one chat.
      message.setChatId(chat.id);

      dispatcher.dispatch({
        type: ActionTypes.SENT_MESSAGE,
        message,
      });
      return chat;
    });
  },

  resetStore(): void {
    dispatcher.dispatch({
      type: ActionTypes.RESET_STORE,
    });
  },

  changeMemberStatus(chat: Chat, membersId: number, state: string): Promise<ChatMembership> {
    return chat.changeMemberStatus(membersId, state).then(membership => {
      dispatcher.dispatch({
        type: ActionTypes.EDIT_MEMBER_FROM_CHAT,
        membership,
        chat,
      });

      return membership;
    });
  },

  addMembersToChat(chat: Chat, membersIds: Array<number>) {
    return chat.addMembers(membersIds).then(memberships => {
      dispatcher.dispatch({
        type: ActionTypes.ADD_MEMBERS_TO_CHAT,
        memberships,
        chat,
      });

      return memberships;
    });
  },

  loadChats: (group: Group): void => {
    // This promise is memoized so that this method doesn't hit the network
    // too frequently.
    ChatActions.promiseLoadChats(group);
  },

  promiseLoadChats: memoize((group: Group): Promise<Chats> => {
    const groupId = group.id;
    return Chats.loadForGroup(groupId).then(chats => {
      loadedChatRangeMap.setLoadedChatRangeForGroup(groupId, chats);

      reduxDispatcher.dispatch({
        type: 'chats/LIST_CHATS',
        chats,
      });

      dispatcher.dispatch({
        type: ActionTypes.LIST_CHATS,
        groupId,
        chats,
      });
      return chats;
    })
    .catch(err => {
      ToastActions.showErrorToast(
        'Messages could not be loaded. Please wait and try again later.'
      );
      console.warn(err);
    });
  }, {
    promise: true,
    maxAge: DATA_RELOAD_TIMEOUT_MS,
  }),

  loadMoreChats(group: Group): void {
    const { oldestUpdate, newestUpdate } = loadedChatRangeMap.getLoadedChatRangeForGroup(group.id);

    ChatActions.loadChatPage(
      group.id,
      oldestUpdate ? oldestUpdate.toISOString() : null,
      newestUpdate ? newestUpdate.toISOString() : null
    );
  },

  loadChatPage: memoize((groupId: number, updatedBefore: ?string, lastUpdated: ?string): void => {
    const data = {};
    if (updatedBefore) {
      data['max_ts'] = updatedBefore;
    }
    if (lastUpdated) {
      data['last_updated_ts'] = lastUpdated;
    }
    if (groupId !== rootGroup.id) {
      data['group_id'] = groupId;
    }
    data.limit = CHATS_PAGE_SIZE;
    // postpone loading this until a specific chat view is requesting it
    data.include_memberships = false;

    const params = {
      data,
    };

    Chats.fetch(params).then(chats => {
      loadedChatRangeMap.setLoadedChatRangeForGroup(groupId, chats);
      reduxDispatcher.dispatch({
        type: 'chats/APPEND_CHATS',
        chats,
      });
      dispatcher.dispatch({
        groupId,
        type: ActionTypes.APPEND_CHATS,
        chats,
        ...params,
      });
    }).catch(e => console.error('Chats.fetch failed', e));
  }, {
    maxAge: DATA_RELOAD_TIMEOUT_MS,
  }),

  searchChats: _.debounce(memoize((query) => {
    Chats.search(query).then(chats => {
      dispatcher.dispatch({
        type: ActionTypes.SEARCH_CHATS,
        chats,
      });
    }).catch(e => console.error('Chats.search failed', e));
  }, {
    maxAge: DATA_RELOAD_TIMEOUT_MS,
  }), SEARCH_NETWORK_LIMIT_MS),

  resetSelection(): void {
    dispatcher.dispatch({
      type: ActionTypes.RESET_SELECTION,
    });
  },

  selectChat(chatId: string): void {
    dispatcher.dispatch({
      type: ActionTypes.SELECT_CHAT,
      chatId,
    });
  },

  loadLocal(members: Array<PotentialOrRegularChatMember>,
    title: string, originIds: number[] = []): void {
    dispatcher.dispatch({
      type: ActionTypes.LOAD_LOCAL_CHAT,
      members,
      title,
      originIds,
    });
  },

  updateLocal(): void {
    dispatcher.dispatch({
      type: ActionTypes.UPDATE_LOCAL_CHAT,
    });
  },

  destroyLocal(): void {
    dispatcher.dispatch({
      type: ActionTypes.DESTROY_LOCAL_CHAT,
    });
  },

  loadChat(chatId: string): Promise<void> {
    return Chat.fetch({ uuid: chatId }).then(chat => {
      reduxDispatcher.dispatch({
        type: 'chats/LOAD_CHAT',
        chat,
      });
      dispatcher.dispatch({
        type: ActionTypes.LOAD_CHAT,
        chat,
      });
    });
  },

  markRead(chatId: string): void {
    dispatcher.dispatch({
      type: ActionTypes.MARK_READ,
      chatId,
    });
  },

  markReadUpto(chatId: string, seqId: number): void {
    ChatActions.markRead(chatId);
    Chat.markReadUpto(chatId, seqId).then(() => {
      dispatcher.dispatch({
        type: ActionTypes.READ_MESSAGE,
        chatId,
      });
    }).catch(e => console.error('Chat.markReadUpTo failed', e));
  },

  disableReplies(chatToDisable: Chat): void {
    chatToDisable.disableReplies().then(chat => {
      ToastActions.showSuccessToast('Replies are turned off');
      dispatcher.dispatch({
        type: ActionTypes.UPDATE_CHAT,
        chat,
      });
    }).catch(e => console.error('Chat.disableReplies failed', e));
  },

  enableReplies(chatToEnable: Chat): void {
    chatToEnable.enableReplies().then(chat => {
      ToastActions.showSuccessToast('Replies are turned on');
      dispatcher.dispatch({
        type: ActionTypes.UPDATE_CHAT,
        chat,
      });
    }).catch(e => console.error('Chat.enableReplies failed', e));
  },

  leaveChat(chat: Chat): void {
    chat.leave().then(() => {
      dispatcher.dispatch({
        type: ActionTypes.UPDATE_CHAT,
        chat,
      });
    }).catch(e => console.error('Chat.leave failed', e));
  },

  updateChatTitle(chat: Chat, title: string): void {
    chat.updateTitle(title).then(() => {
      dispatcher.dispatch({
        type: ActionTypes.UPDATE_CHAT,
        chat,
      });
    }).catch(e => console.error('Chat.updateTitle failed', e));
  },

  // TODO remove because each PCM query is unique. It's not just loading data
  // into a store, the order of the results is significant.
  loadPotentialChatMembers: _.debounce(function(query) {
    ChatActions.promisePotentialChatMembers(query);
  }, SEARCH_NETWORK_LIMIT_MS),

  promisePotentialChatMembers: memoize((query) => {
    return PotentialChatMembers.fetch({
      data: {
        q: query,
      },
    }).then(members => {
      dispatcher.dispatch({
        type: ActionTypes.LOAD_MEMBERS,
        members,
        query,
      });
      return members;
    });
  }, {
    maxAge: DATA_RELOAD_TIMEOUT_MS,
  }),

  emailTranscript(chatId: string): void {
    Chat.emailTranscript(chatId).then(() => {
      ToastActions.showSuccessToast('We’ve sent the message history for this conversation.');
    }).catch(error => {
      ToastActions.showAPIError(error);
      throw error;
    });
  },

  reportChat(chatId: string, uuid: string, reason: string, notes: ?string): void {
    Chat.reportChat(chatId, uuid, reason, notes).then(() => {
      ToastActions.showSuccessToast('Report submitted');
    }).catch(e => console.error('Chat.reportChat failed', e));
  },

  requestMemberAge(memberToRequest: PotentialChatMember): void {
    memberToRequest.requestAge().then(member => {
      dispatcher.dispatch({
        type: ActionTypes.UPDATE_MEMBER,
        member,
      });
    }).catch(e => console.error('memberToRequest.requestAge failed', e));
  },

  setFocusView(focus: number): void {
    dispatcher.dispatch({
      type: ActionTypes.SET_FOCUS_VIEW,
      focus,
    });
  },

  enableRepliesForAllChats(): Promise<void> {
    return Chats.enableRepliesForAllChats();
  },

  disableRepliesForAllChats(): Promise<void> {
    return Chats.disableRepliesForAllChats();
  },

  resetPromiseMemoization(): void {
    ChatActions.promiseLoadChats.clear();
    ChatActions.promisePotentialChatMembers.clear();
    loadedChatRangeMap.map = {};
  },
};

export default ChatActions;



// WEBPACK FOOTER //
// ./src/chat/actions/ChatActions.js